import React from 'react';
import {
    Container,
    Grid,
    Card,
    CardContent,
    Typography,
    Button,
    Box,
    Chip,
} from '@mui/material';
import { CheckCircleOutline } from '@mui/icons-material';

interface PricingSectionProps {
    onGetStarted: (planName: string) => void;
}

interface Plan {
    name: string;
    price: string;
    priceCaption: string;
    tagline: string;
    features: string[];
    cta: string;
    highlighted?: boolean;
}

const plans: Plan[] = [
    {
        name: 'Free',
        price: '৳0',
        priceCaption: 'up to 50 employees',
        tagline: 'See the benefit before you pay a taka.',
        features: [
            'Attendance tracking',
            'Leave management',
            'Bangladesh holiday calendar',
            'Employee profiles',
        ],
        cta: 'Start Free',
    },
    {
        name: 'Core HR',
        price: '৳15',
        priceCaption: 'per employee/month',
        tagline: 'Less than a cup of tea per employee.',
        features: [
            'Everything in Free, unlimited employees',
            'ZKT fingerprint device sync',
            'Shift management',
            'Employee documents',
            'Dashboards & reports',
        ],
        cta: 'Get Started',
    },
    {
        name: 'Business',
        price: '৳30',
        priceCaption: 'per employee/month',
        tagline: 'Core HR plus the business modules below — or add them one at a time.',
        features: [
            'Core HR included',
            'Payroll',
            'Expense claims',
            'Accounting & VAT',
            'Inventory & stores',
            'Procurement',
            'Fixed assets',
            'Sales & CRM',
            'Merchandising & costing',
            'Export & LC',
            'Maintenance',
            'Owner dashboard',
        ],
        cta: 'Get Started',
        highlighted: true,
    },
    {
        name: 'Enterprise',
        price: 'Custom',
        priceCaption: 'pricing',
        tagline: 'For large factories and groups. A price tailored to your company: your size, your modules, your rollout.',
        features: [
            'HR, payroll, accounting & VAT, inventory, procurement, fixed assets, sales & CRM, merchandising, export & LC, maintenance',
            'ZKT rollout across all gates',
            'Data import & on-site training',
            'Dedicated support for your team',
        ],
        cta: 'Contact Us',
    },
];

const PricingSection: React.FC<PricingSectionProps> = ({ onGetStarted }) => (
    <section className="landing-pricing" id="pricing">
        <Container maxWidth="lg">
            <Box textAlign="center" mb={6}>
                <h2 className="landing-section-title">Simple Per-Employee Pricing</h2>
                <Typography variant="body1" color="text.secondary" maxWidth={600} mx="auto">
                    Pay only for the modules you enable, priced per employee.
                    Grow bigger, pay less: 40% off employees 251–1,000, and deeper
                    discounts beyond that. Large factories and groups get Enterprise
                    pricing tailored to the company.
                </Typography>
            </Box>
            <Grid container spacing={3} alignItems="stretch">
                {plans.map((plan) => (
                    <Grid size={{ xs: 12, sm: 6, md: 3 }} key={plan.name}>
                        <Card
                            sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                position: 'relative',
                                border: plan.highlighted ? '2px solid #007bff' : '1px solid #e0e0e0',
                                transition: 'transform 0.2s, box-shadow 0.2s',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                                },
                            }}
                            elevation={plan.highlighted ? 3 : 1}
                        >
                            {plan.highlighted && (
                                <Chip
                                    label="Best value"
                                    color="primary"
                                    size="small"
                                    sx={{
                                        position: 'absolute',
                                        top: 12,
                                        right: 12,
                                        fontWeight: 600,
                                    }}
                                />
                            )}
                            <CardContent
                                sx={{ p: 3, display: 'flex', flexDirection: 'column', flexGrow: 1 }}
                            >
                                <Typography variant="h6" fontWeight={600}>
                                    {plan.name}
                                </Typography>
                                <Box display="flex" alignItems="baseline" gap={1} my={1}>
                                    <Typography variant="h4" fontWeight={700} color="#007bff">
                                        {plan.price}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {plan.priceCaption}
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary" mb={2}>
                                    {plan.tagline}
                                </Typography>
                                <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, flexGrow: 1 }}>
                                    {plan.features.map((feature) => (
                                        <Box
                                            component="li"
                                            key={feature}
                                            sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                                        >
                                            <CheckCircleOutline sx={{ fontSize: 18, color: '#28a745' }} />
                                            <Typography variant="body2">{feature}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                                <Button
                                    variant={plan.highlighted ? 'contained' : 'outlined'}
                                    fullWidth
                                    onClick={() => onGetStarted(plan.name)}
                                    sx={{ mt: 2, textTransform: 'none', fontWeight: 600 }}
                                >
                                    {plan.cta}
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
            <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                textAlign="center"
                mt={3}
            >
                Overall minimum bill ৳500/month. Accounting and Inventory carry small module
                minimums (৳400 and ৳300/month) for teams under ~35 employees. Free plan does
                not include payroll or fingerprint device sync.
            </Typography>
        </Container>
    </section>
);

export default PricingSection;
